import { useEffect, useRef } from "react";
import * as api from "../../api";

export const LIVE_PRICING_INTERVAL_MS = 15000;

/**
 * Mantiene los precios al día sin recargar la página: sondea la huella ligera
 * `/pricing-version` (tasa + precios/disponibilidad) cada 15 s mientras la pestaña
 * está visible, y de inmediato al volver a la pestaña, al recuperar la conexión o al
 * enfocar la ventana. La primera respuesta también reconcilia tasa y catálogo: una
 * tasa cacheada nunca puede sobrevivir sólo porque aún no había huella previa.
 * Funciona para visitantes y clientes logueados, durante horas o días.
 */
export function useLivePricing(onChange) {
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const versionRef = useRef(null);
  const busyRef = useRef(false);

  useEffect(() => {
    const check = async () => {
      if (busyRef.current || document.visibilityState === "hidden") return;
      busyRef.current = true;
      try {
        const payload = await api.getPricingVersion();
        if (!payload?.version) return;
        const previous = versionRef.current;
        versionRef.current = payload.version;
        if (previous === null || previous !== payload.version) {
          await onChangeRef.current?.(payload);
        }
      } catch (e) {
        console.warn("live pricing check failed:", e);
      } finally {
        busyRef.current = false;
      }
    };
    check();
    const timer = setInterval(check, LIVE_PRICING_INTERVAL_MS);
    const onVisible = () => { if (document.visibilityState === "visible") check(); };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", check);
    window.addEventListener("online", check);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", check);
      window.removeEventListener("online", check);
    };
  }, []);
}
